import { Helpers } from '../helpers/Helpers';
import { ILongRunningAction, LongRunningActionState, LongRunningActionType } from '../models/ILongRunningAction';
import { Module } from '../Module';
import { GetActionInfoRequest } from '../ServerApi/requests/GetActionInfoRequest';
import { GetBackgroundActionInfoRequest } from '../ServerApi/requests/GetBackgroundActionInfoRequest';
import { IActionInfo, IGetActionInfoResult } from '../ServerApi/types/IActionInfo';
import { IActionResult } from '../ServerApi/types/IActionResult';
import { IRequest } from '../ServerApi/types/IRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from './ErrorNotifyingServer';
import { IPoller, PollService } from './PollService';
import { ErrorReason, ServerService } from './ServerService';

export class LongRunningActionService {

  public static $inject = ['ServerService', 'PollService', '$q', '$timeout'];

  private StringToStateDict: {[stringType: string]: LongRunningActionState} = {
    queued : LongRunningActionState.Queued,
    inProgress : LongRunningActionState.InProgress,
    completed: LongRunningActionState.Complete
  };

  private StringToTypeDict: {[stringType: string]: LongRunningActionType} = {
    rebuildSearchIndex: LongRunningActionType.RebuildSearchIndex,
    deleteSites: LongRunningActionType.DeleteSites
  };

  // Inverted StringToTypeDict
  private TypeToStringDict: {[type: number]: string } = Helpers.swapDict(this.StringToTypeDict);

  constructor(private server: ServerService,
              private pollService: PollService,
              private $q: ng.IQService,
              private $timeout: ng.ITimeoutService) {}

  /**
   * Get the last LongRunningAction of a type.
   * Clients should handle promise rejection.
   */
  public getLastByType(type: LongRunningActionType): ng.IPromise<ILongRunningAction> {
    const stringType = this.TypeToStringDict[type];
    return this.server.sendRequest(new GetActionInfoRequest({actionType: stringType}))
      .then((result: IGetActionInfoResult) => {
        if (!result.actions || !result.actions.length) {
          return null;
        }
        return this.toLongRunningAction(result.actions[0]);
      });
  }

  public trackBackgroundJobProgress(id: string): IPoller<ILongRunningAction> {
    return this.trackProgress(new GetBackgroundActionInfoRequest({actionId: id}));
  }

  public trackAsyncJobProgress(id: string): IPoller<ILongRunningAction> {
    return this.trackProgress(new GetActionInfoRequest({actionId: id}));
  }

  /**
   * Track the progress of a LongRunningAction
   * The promise will be fullfilled with the LongRunningAction when it is complete
   * The promise will be rejected with ResourceNotFound if there is no action of given id
   */
  private trackProgress<TParams>(request: IRequest<TParams, IGetActionInfoResult>): IPoller<ILongRunningAction> {
    const isComplete = (result: IGetActionInfoResult) => {
      if (!result.actions || !result.actions.length) {
        return true;
      } else {
        return this.StringToStateDict[result.actions[0].state] === LongRunningActionState.Complete;
      }
    };

    const actionInfoPoller: IPoller<IGetActionInfoResult> = this.pollService.poll(request, isComplete);

    const lrActionPromise: ng.IPromise<any> = actionInfoPoller.promise
      .then((result: IGetActionInfoResult) => {
        if (!result.actions || !result.actions.length) {
          return this.$q.reject(ErrorReason.ResourceNotFound);
        }

        return this.$q.when(this.toLongRunningAction(result.actions[0]));
      })
      .progress((result: IGetActionInfoResult) => {
        return this.toLongRunningAction(result.actions[0]);
      });

    // We have to return a new IPoller because type of the promise changes
    const lrActionPoller: IPoller<ILongRunningAction> = {
      promise: lrActionPromise,
      cancel: actionInfoPoller.cancel
    };
    return lrActionPoller;
  }

  // Pass in an optional method to catch the poller if it's needed to cancel the request
  public startProgressTrackingForResult<TResult extends IResult>(
    getPoller: (poller: IPoller<ILongRunningAction>) => any = angular.noop
  ): (result: IActionResult) => ng.IPromise<TResult> {
    return (result: IActionResult): ng.IPromise<TResult> => {
      if (!result.actionId) {
        return this.$q.reject(result);
      }

      const poller = this.trackAsyncJobProgress(result.actionId);
      getPoller(poller);

      return poller.promise.then((longRunningAction: ILongRunningAction) => {
        // TODO: Is ILongRunningAction.response an IResponse or an IResult?
        return longRunningAction.response;
      });
    };
  }

  // TODO: This is temporary to get this method to work with the current APIs
  //       This method should be removed once there are consistent responses for long running actions.
  public startProgressTrackingForResponse<TResult extends IResult>(
    getPoller: (poller: IPoller<ILongRunningAction>) => any = angular.noop
  ): (result: IActionResult) => ng.IPromise<TResult> {
    return (result: IActionResult): ng.IPromise<TResult> => {
      const poller = this.trackAsyncJobProgress(result.actionId);
      getPoller(poller);

      return poller.promise.then((longRunningAction: ILongRunningAction) => {
        const response = longRunningAction.response;
        return response ? response.result : undefined;
      });
    };
  }

  // Convert a server result to an ILongRunningAction
  private toLongRunningAction<T>(actionInfo: IActionInfo): ILongRunningAction {
    const action: ILongRunningAction = {
      id: actionInfo.id,
      type: this.StringToTypeDict[actionInfo.type],
      state: this.StringToStateDict[actionInfo.state],
      percentComplete: actionInfo.percentComplete,
      response: actionInfo.response,
      succeeded: actionInfo.succeeded
    };
    return action;
  }

  // Used if a list is refreshed.  This is needed
  public delayResultForSolr<T>(): (result: T) => ng.IPromise<T> {
    return (result: T): ng.IPromise<T> => {
      return this.$timeout(() => result, ErrorNotifyingServer.ResponseDelay);
    };
  }

}

Module.instance().service('LongRunningActionService', LongRunningActionService);
